import React from 'react'

import { Link } from 'react-router-dom'
import { Form } from 'react-final-form'
import { Autocomplete } from 'mui-rff'
import { Box, CircularProgress, ListItem, ListItemIcon, ListItemText, styled } from '@mui/material'
import { Dashboard as DashboardIcon, PersonOutline as PersonOutlineIcon } from '@mui/icons-material'

import { formatName, useSearchParams } from '@edge/spark/utils'
import { useOtherUsersDashboardsQuery, type UserAttributes } from '@edge/spark/io-dashboards'

// import { useOtherUsersDashboardsQuery, UserAttributes } from '../../../../generated/graphql'

interface DLBUIDProps {
    userId: UserAttributes['authenticationId']
}

const DashboardLinksByUserId: React.FC<DLBUIDProps> = ({ userId }: DLBUIDProps) => {
    const { data, loading } = useOtherUsersDashboardsQuery({
        // @ts-expect-error graphql-tools needs to be aware of @export
        variables: { userId },
    })

    const parsedDashboards = data?.administrator?.user?.dashboards?.map((d) => JSON.parse(d || 'null')) || []

    const sortedDashboards = parsedDashboards.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
            sensitivity: 'base',
        }),
    )

    return (
        <>
            {loading && !parsedDashboards.length && (
                <ListItem button disabled>
                    <ListItemIcon>
                        <CircularProgress size={28} />
                    </ListItemIcon>
                    <ListItemText primary="Loading" />
                </ListItem>
            )}

            {!loading && !parsedDashboards.length && (
                <ListItem
                    component={Link}
                    to={`/dashboards/00000000-0000-1000-8000-000000000000${userId ? `?uid=${userId}` : ''}`}
                    button
                >
                    <ListItemIcon>
                        <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard" />
                </ListItem>
            )}

            {sortedDashboards.map((d) => (
                <ListItem
                    component={Link}
                    to={d.id ? `/dashboards/${d.id}${userId ? `?uid=${userId}` : ''}` : ''}
                    key={d.id}
                    button
                >
                    <ListItemIcon>
                        <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText primary={d.name} />
                </ListItem>
            ))}
        </>
    )
}

const SmallListItemIcon = styled(ListItemIcon)`
    min-width: 36px;
`

const UserAvatar: React.FC = () => (
    <SmallListItemIcon>
        <PersonOutlineIcon fontSize="small" />
    </SmallListItemIcon>
)

interface Props {
    users?: UserAttributes[] | null
}

export const AdminAuctionUserDashboards: React.FC<Props> = ({ users }: Props) => {
    const { uid } = useSearchParams()

    const sortedUsers = users?.sort((a, b) =>
        formatName(a).localeCompare(formatName(b), undefined, {
            sensitivity: 'base',
        }),
    )

    if (!sortedUsers) {
        return null
    }

    return (
        <ListItem component="div">
            <Form
                initialValues={{
                    authenticationId: uid,
                }}
                onSubmit={(): void => undefined}
                render={({ values }) => (
                    <Box sx={{ flex: 1 }}>
                        <Autocomplete
                            size="small"
                            label="Others' Dashboards"
                            name="authenticationId"
                            options={sortedUsers}
                            // String type regression
                            // https://github.com/mui/material-ui/issues/23708
                            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : formatName(opt))}
                            getOptionValue={({ authenticationId }: UserAttributes): string | null | undefined =>
                                authenticationId
                            }
                            textFieldProps={{ fullWidth: true, variant: 'outlined' }}
                            renderOption={(props, opt) => (
                                <li {...props}>
                                    <UserAvatar />
                                    <ListItemText>{formatName(opt)}</ListItemText>
                                </li>
                            )}
                        />
                        {values.authenticationId && <DashboardLinksByUserId userId={values.authenticationId} />}
                    </Box>
                )}
            />
        </ListItem>
    )
}

export default AdminAuctionUserDashboards
